import type {
  ApiEnvelope,
  DistributionLink,
  EquipmentChecklistItemPhoto,
  EquipmentDemoCatalog,
  EquipmentDemoClassifyResult,
  EquipmentDemoDeficiencyAnalysis,
  EquipmentDemoSubmission,
  EquipmentDemoSubmissionSummary,
  FetchSource,
} from "@/lib/types";

export const API_BASE = "/api-proxy/v1";

type StrictApiResult<T> = {
  data: T | null;
  source: FetchSource;
  error?: string;
};

function unwrapResponse<T>(payload: unknown): T {
  if (payload && typeof payload === "object" && "data" in payload) {
    const envelope = payload as ApiEnvelope<T>;
    if (envelope.data !== undefined) {
      return envelope.data;
    }
  }
  return payload as T;
}

async function parseJson<T>(response: Response): Promise<T> {
  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    throw new Error(`Expected JSON response but got ${contentType || "unknown content type"}`);
  }
  return unwrapResponse<T>(await response.json());
}

async function parseErrorMessage(response: Response): Promise<string> {
  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    try {
      const raw = await response.json();
      const detail = raw?.detail ?? raw;
      const errorCode = typeof detail?.error_code === "string" ? detail.error_code : "";
      const message =
        (typeof detail?.message === "string" && detail.message) ||
        (typeof raw?.message === "string" && raw.message) ||
        `HTTP ${response.status}`;
      const cause = typeof detail?.cause === "string" ? detail.cause : "";
      return [errorCode ? `[${errorCode}]` : "", message, cause && cause !== message ? `원인: ${cause}` : ""]
        .filter(Boolean)
        .join(" ");
    } catch {
      return `HTTP ${response.status}`;
    }
  }
  const text = await response.text();
  return text ? `HTTP ${response.status}: ${text}` : `HTTP ${response.status}`;
}

async function fetchJsonStrict<T>(path: string, init?: RequestInit): Promise<StrictApiResult<T>> {
  try {
    const response = await fetch(`${API_BASE}${path}`, {
      headers: {
        Accept: "application/json",
        ...(init?.headers || {}),
      },
      ...init,
    });
    if (!response.ok) {
      return { data: null, source: "api", error: await parseErrorMessage(response) };
    }
    return { data: await parseJson<T>(response), source: "api" };
  } catch (error) {
    return { data: null, source: "api", error: error instanceof Error ? error.message : String(error) };
  }
}

function browserOrigin() {
  return typeof window === "undefined" ? "" : window.location.origin;
}

function normalizeDistributionLink(item: DistributionLink): DistributionLink {
  const origin = browserOrigin();
  const token = item.distribution_token;
  const linkId = item.distribution_link_id;
  return {
    ...item,
    access_url: origin && token ? `${origin}/access/${encodeURIComponent(token)}` : item.access_url,
    qr_image_url: linkId ? `${API_BASE}/equipment-demo/links/${encodeURIComponent(linkId)}/qr` : item.qr_image_url,
  };
}

function normalizeDistributionLinks(items: DistributionLink[]) {
  return items.map(normalizeDistributionLink);
}

export async function loadEquipmentDemoCatalog() {
  return fetchJsonStrict<EquipmentDemoCatalog>("/equipment-demo/catalog");
}

export async function classifyEquipmentDemoImage(
  formData: FormData,
  options?: {
    distributionToken?: string;
    distributionLinkId?: string;
  },
) {
  if (options?.distributionToken) {
    formData.append("distribution_token", options.distributionToken);
  }
  if (options?.distributionLinkId) {
    formData.append("distribution_link_id", options.distributionLinkId);
  }
  return fetchJsonStrict<EquipmentDemoClassifyResult>("/equipment-demo/classify", {
    method: "POST",
    body: formData,
  });
}

export async function confirmEquipmentDemoSubmission(payload: unknown) {
  return fetchJsonStrict<EquipmentDemoSubmission>("/equipment-demo/confirm", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export async function uploadEquipmentDemoChecklistPhoto(draftId: string, formData: FormData) {
  return fetchJsonStrict<EquipmentChecklistItemPhoto>(`/equipment-demo/drafts/${encodeURIComponent(draftId)}/checklist-photos`, {
    method: "POST",
    body: formData,
  });
}

export async function supplementEquipmentDemoSubmissionPhotos(submissionId: string, formData: FormData) {
  return fetchJsonStrict<EquipmentDemoSubmission>(`/equipment-demo/submissions/${encodeURIComponent(submissionId)}/photos`, {
    method: "POST",
    body: formData,
  });
}

export async function reviewEquipmentDemoSubmissionPhotos(submissionId: string, force = false) {
  return fetchJsonStrict<EquipmentDemoSubmission>(`/equipment-demo/submissions/${encodeURIComponent(submissionId)}/checklist-photo-reviews`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ force }),
  });
}

export async function loadEquipmentDemoSubmissions(
  limit = 100,
  filters?: {
    linkId?: string;
    linkCode?: string;
    recipientLabel?: string;
  },
) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (filters?.linkId) params.set("link_id", filters.linkId);
  if (filters?.linkCode) params.set("link_code", filters.linkCode);
  if (filters?.recipientLabel) params.set("recipient_label", filters.recipientLabel);
  return fetchJsonStrict<EquipmentDemoSubmissionSummary[]>(`/equipment-demo/submissions?${params.toString()}`);
}

export async function loadEquipmentDemoDeficiencyAnalysis(limit = 1000) {
  const params = new URLSearchParams({ limit: String(limit) });
  return fetchJsonStrict<EquipmentDemoDeficiencyAnalysis>(`/equipment-demo/analysis/deficiencies?${params.toString()}`);
}

export async function loadEquipmentDemoSubmission(submissionId: string) {
  return fetchJsonStrict<EquipmentDemoSubmission>(`/equipment-demo/submissions/${encodeURIComponent(submissionId)}`);
}

export async function deleteEquipmentDemoSubmission(submissionId: string) {
  return fetchJsonStrict<{ submission_id: string }>(`/equipment-demo/submissions/${encodeURIComponent(submissionId)}`, {
    method: "DELETE",
  });
}

export async function createEquipmentDemoDistributionLink(payload: { recipient_label?: string; admin_note?: string }) {
  const result = await fetchJsonStrict<DistributionLink>("/equipment-demo/links", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  return result.data ? { ...result, data: normalizeDistributionLink(result.data) } : result;
}

export async function loadEquipmentDemoDistributionLinks() {
  const result = await fetchJsonStrict<DistributionLink[]>("/equipment-demo/links");
  return result.data ? { ...result, data: normalizeDistributionLinks(result.data) } : result;
}

export async function updateEquipmentDemoDistributionLinkStatus(linkId: string, status: "active" | "inactive") {
  const result = await fetchJsonStrict<DistributionLink>(`/equipment-demo/links/${encodeURIComponent(linkId)}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ status }),
  });
  return result.data ? { ...result, data: normalizeDistributionLink(result.data) } : result;
}

export async function deleteEquipmentDemoDistributionLink(linkId: string) {
  return fetchJsonStrict<{ distribution_link_id: string }>(`/equipment-demo/links/${encodeURIComponent(linkId)}`, {
    method: "DELETE",
  });
}

export async function loadEquipmentDemoDistributionLinkByToken(token: string) {
  const result = await fetchJsonStrict<DistributionLink>(`/equipment-demo/links/by-token/${encodeURIComponent(token)}`);
  return result.data ? { ...result, data: normalizeDistributionLink(result.data) } : result;
}
